import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { Menu, X } from 'lucide-react';

export default function Navigation() {
  const { lang, toggleLang } = useLanguage();
  const t = translations.nav[lang];
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const links = [
    { id: 'product', label: t.highlight },
    { id: 'about', label: t.about },
    { id: 'services', label: t.services },
    { id: 'benefits', label: t.benefits },
    { id: 'target', label: t.target },
    { id: 'references', label: t.references },
    { id: 'contact', label: t.contact },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-lg border-b border-slate-200/50'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <button onClick={() => scrollTo('hero')} className="flex items-center" data-testid="nav-logo">
            <img src={scrolled ? '/logo-monogram.png' : '/logo-monogram-white.png'} alt="ENBI" className="h-10 w-10 transition-all duration-300" />
          </button>

          <div className="hidden lg:flex items-center gap-8">
            {links.map(l => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                data-testid={`nav-${l.id}`}
                className={`text-xs tracking-[0.2em] uppercase font-semibold transition-colors hover:text-enbi-gold ${
                  scrolled ? 'text-enbi-navy' : 'text-white/90'
                }`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={toggleLang}
              data-testid="language-toggle"
              className={`px-3 py-1.5 text-xs font-bold tracking-widest border transition-colors ${
                scrolled
                  ? 'border-enbi-navy text-enbi-navy hover:bg-enbi-navy hover:text-white'
                  : 'border-white/60 text-white hover:bg-white/10'
              }`}
            >
              {lang === 'de' ? 'IT' : 'DE'}
            </button>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen
              ? <X className={scrolled ? 'text-enbi-navy' : 'text-white'} size={24} />
              : <Menu className={scrolled ? 'text-enbi-navy' : 'text-white'} size={24} />
            }
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {links.map(l => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="block w-full text-left text-sm tracking-[0.15em] uppercase font-semibold text-enbi-navy hover:text-enbi-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => { toggleLang(); setMobileOpen(false); }}
              className="block text-sm font-bold tracking-widest text-enbi-gold"
            >
              {lang === 'de' ? 'Italiano' : 'Deutsch'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
